import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Button,
  FileInput,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconDownload,
  IconFile,
  IconPlus,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import usePermissions from "../../../hooks/usePermissions";
import { useTranslation } from "../../../hooks/useTranslation";
import * as exceptionReportService from "../../../services/CityObjectExceptionReportService";
import { CityObjectExceptionReportType } from "../../../types/CityObjectExceptionReport";
import { ObjectData } from "../types/ObjectFormTypes";
import classes from "./ExceptionReportsTab.module.css";

interface ExceptionReportsTabProps {
  form: UseFormReturnType<ObjectData>;
  addressSystemId: number;
  userId: number;
}

interface UploadFormValues {
  file: File | null;
  fileHeader: string;
}

const DEFAULT_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".bmp",
  ".webp",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".txt",
];
const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024;

export default function ExceptionReportsTab({
  form,
  addressSystemId,
  userId,
}: Readonly<ExceptionReportsTabProps>) {
  const { checkAccess } = usePermissions();
  const canModify = checkAccess("CITY_OBJECT_EXCEPTION_REPORT_MODIFY");
  const { getTranslation } = useTranslation({
    pageId: 517,
    addressSystemId,
    autoLoad: true,
  });

  const objectId = form.values.id || 0;
  const contractId = Number.parseInt(form.values.job, 10) || 0;
  const [reports, setReports] = useState<CityObjectExceptionReportType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadOpened, setUploadOpened] = useState(false);
  const [allowedExtensions, setAllowedExtensions] = useState(
    DEFAULT_ALLOWED_EXTENSIONS,
  );
  const [maxFileSize, setMaxFileSize] = useState(DEFAULT_MAX_FILE_SIZE);
  const [reportToDelete, setReportToDelete] =
    useState<CityObjectExceptionReportType | null>(null);

  const uploadForm = useForm<UploadFormValues>({
    initialValues: {
      file: null,
      fileHeader: "",
    },
    validate: {
      file: (file) =>
        file
          ? null
          : getTranslation("EXCEPTION_REPORT_VALIDATION_FILE_REQUIRED") ||
            "Select a file.",
      fileHeader: (value) =>
        value.trim()
          ? null
          : getTranslation("EXCEPTION_REPORT_VALIDATION_TITLE_REQUIRED") ||
            "Enter a title.",
    },
  });

  const showError = useCallback(
    (message: string) => {
      notifications.show({
        title:
          getTranslation("EXCEPTION_REPORT_TITLE_ERROR") || "Error",
        message,
        color: "red",
        position: "top-center",
      });
    },
    [getTranslation],
  );

  const loadReports = useCallback(async () => {
    if (objectId <= 0 || contractId <= 0 || addressSystemId <= 0 || userId <= 0)
      return;

    setLoading(true);
    try {
      const [loadedReports, uploadPolicy] = await Promise.all([
        exceptionReportService.getExceptionReports(
          objectId,
          contractId,
          addressSystemId,
          userId,
        ),
        exceptionReportService.getUploadPolicy(
          objectId,
          contractId,
          addressSystemId,
          userId,
        ),
      ]);
      setReports(loadedReports);
      setAllowedExtensions(uploadPolicy.allowedExtensions);
      setMaxFileSize(uploadPolicy.maxFileSizeBytes);
    } catch {
      showError(
        getTranslation("EXCEPTION_REPORT_MESSAGE_LOAD_FAILED") ||
          "Failed to load exception reports.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    addressSystemId,
    contractId,
    getTranslation,
    objectId,
    showError,
    userId,
  ]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const closeUpload = () => {
    setUploadOpened(false);
    uploadForm.reset();
  };

  const validateSelectedFile = (file: File): string | null => {
    const dotIndex = file.name.lastIndexOf(".");
    const extension =
      dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : "";
    if (!allowedExtensions.includes(extension)) {
      return (
        getTranslation("EXCEPTION_REPORT_VALIDATION_EXTENSION") ||
        "This file extension is not allowed."
      );
    }
    if (file.size <= 0) {
      return (
        getTranslation("EXCEPTION_REPORT_VALIDATION_EMPTY_FILE") ||
        "The selected file is empty."
      );
    }
    if (file.size > maxFileSize) {
      const template =
        getTranslation("EXCEPTION_REPORT_VALIDATION_FILE_SIZE") ||
        "The selected file exceeds the {size} limit.";
      return template.replace("{size}", formatSize(maxFileSize));
    }
    return null;
  };

  const handleUpload = uploadForm.onSubmit(async (values) => {
    if (!values.file) return;
    const fileError = validateSelectedFile(values.file);
    if (fileError) {
      uploadForm.setFieldError("file", fileError);
      return;
    }

    setSaving(true);
    try {
      await exceptionReportService.uploadExceptionReport({
        objectId,
        contractId,
        addressSystemId,
        currentUserId: userId,
        file: values.file,
        fileHeader: values.fileHeader.trim(),
      });
      notifications.show({
        title:
          getTranslation("EXCEPTION_REPORT_TITLE_SUCCESS") || "Success",
        message:
          getTranslation("EXCEPTION_REPORT_MESSAGE_UPLOADED") ||
          "exception report uploaded successfully.",
        color: "green",
        position: "top-center",
      });
      closeUpload();
      await loadReports();
    } catch {
      showError(
        getTranslation("EXCEPTION_REPORT_MESSAGE_UPLOAD_FAILED") ||
          "Failed to upload the exception report.",
      );
    } finally {
      setSaving(false);
    }
  });

  const handleDownload = async (report: CityObjectExceptionReportType) => {
    try {
      const blob = await exceptionReportService.downloadExceptionReport(
        report.id,
        objectId,
        contractId,
        addressSystemId,
        userId,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = report.originalName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      showError(
        getTranslation("EXCEPTION_REPORT_MESSAGE_DOWNLOAD_FAILED") ||
          "Failed to download the exception report.",
      );
    }
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;
    setSaving(true);
    try {
      await exceptionReportService.deleteExceptionReport(
        reportToDelete.id,
        objectId,
        contractId,
        addressSystemId,
        userId,
      );
      notifications.show({
        title:
          getTranslation("EXCEPTION_REPORT_TITLE_SUCCESS") || "Success",
        message:
          getTranslation("EXCEPTION_REPORT_MESSAGE_DELETED") ||
          "exception report deleted successfully.",
        color: "green",
        position: "top-center",
      });
      setReportToDelete(null);
      await loadReports();
    } catch {
      showError(
        getTranslation("EXCEPTION_REPORT_MESSAGE_DELETE_FAILED") ||
          "Failed to delete the exception report.",
      );
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("fi-FI", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <Stack h="100%" gap="md" className={classes.root}>
        <LoadingOverlay visible={loading || saving} zIndex={1000} />
        <Group justify="space-between">
          <Title order={4}>
            {getTranslation("EXCEPTION_REPORT_TITLE_LIST") ||
              "Exception Reports"}
          </Title>
          {canModify && (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setUploadOpened(true)}
            >
              {getTranslation("EXCEPTION_REPORT_ACTION_ADD") || "Add file"}
            </Button>
          )}
        </Group>

        <Paper withBorder radius="md" className={classes.tableContainer}>
          <Table striped highlightOnHover className={classes.table}>
            <Table.Thead className={classes.tableHeader}>
              <Table.Tr>
                <Table.Th className={classes.titleColumn}>
                  {getTranslation("EXCEPTION_REPORT_LABEL_TITLE") || "Title"}
                </Table.Th>
                <Table.Th className={classes.dateColumn}>
                  {getTranslation("EXCEPTION_REPORT_LABEL_DATE") || "Date"}
                </Table.Th>
                <Table.Th className={classes.sizeColumn}>
                  {getTranslation("EXCEPTION_REPORT_LABEL_SIZE") || "Size"}
                </Table.Th>
                <Table.Th className={classes.actionsColumn}>
                  {getTranslation("EXCEPTION_REPORT_LABEL_ACTIONS") ||
                    "Actions"}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {reports.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="xl">
                      {getTranslation("EXCEPTION_REPORT_EMPTY") ||
                        "No files found."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                reports.map((report) => (
                  <Table.Tr key={report.id}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <IconFile size={18} />
                        <Tooltip
                          label={`${report.originalName} (${formatSize(report.size)})`}
                          openDelay={300}
                        >
                          <Text className={classes.fileTitle}>
                            {report.fileHeader || report.originalName}
                          </Text>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td>{formatDate(report.created)}</Table.Td>
                    <Table.Td>{formatSize(report.size)}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="center" wrap="nowrap">
                        <Tooltip
                          label={
                            getTranslation(
                              "EXCEPTION_REPORT_ACTION_DOWNLOAD",
                            ) || "Download"
                          }
                        >
                          <ActionIcon
                            variant="subtle"
                            aria-label={
                              getTranslation(
                                "EXCEPTION_REPORT_ACTION_DOWNLOAD",
                              ) || "Download"
                            }
                            onClick={() => handleDownload(report)}
                          >
                            <IconDownload size={18} />
                          </ActionIcon>
                        </Tooltip>
                        {canModify && (
                          <Tooltip
                            label={
                              getTranslation(
                                "EXCEPTION_REPORT_ACTION_DELETE",
                              ) || "Delete"
                            }
                          >
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={
                                getTranslation(
                                  "EXCEPTION_REPORT_ACTION_DELETE",
                                ) || "Delete"
                              }
                              onClick={() => setReportToDelete(report)}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      </Stack>

      <Modal
        opened={uploadOpened}
        onClose={closeUpload}
        title={
          getTranslation("EXCEPTION_REPORT_TITLE_ADD") ||
          "Add Exception Report"
        }
        centered
      >
        <form onSubmit={handleUpload} noValidate>
          <Stack>
            <FileInput
              label={getTranslation("EXCEPTION_REPORT_LABEL_FILE") || "File"}
              placeholder={
                getTranslation("EXCEPTION_REPORT_PLACEHOLDER_FILE") ||
                "Select a file"
              }
              accept={allowedExtensions.join(",")}
              clearable
              required
              leftSection={<IconUpload size={16} />}
              {...uploadForm.getInputProps("file")}
            />
            <TextInput
              label={
                getTranslation("EXCEPTION_REPORT_LABEL_FILE_TITLE") ||
                "File title"
              }
              required
              maxLength={500}
              {...uploadForm.getInputProps("fileHeader")}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeUpload}>
                {getTranslation("EXCEPTION_REPORT_ACTION_CANCEL") || "Cancel"}
              </Button>
              <Button type="submit" loading={saving}>
                {getTranslation("EXCEPTION_REPORT_ACTION_UPLOAD") || "Upload"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={reportToDelete !== null}
        onClose={() => setReportToDelete(null)}
        title={
          getTranslation("EXCEPTION_REPORT_TITLE_DELETE") || "Delete file"
        }
        centered
        size="sm"
      >
        <Stack>
          <Text>
            {getTranslation("EXCEPTION_REPORT_CONFIRM_DELETE") ||
              "Are you sure you want to delete this file?"}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setReportToDelete(null)}>
              {getTranslation("EXCEPTION_REPORT_ACTION_CANCEL") || "Cancel"}
            </Button>
            <Button color="red" loading={saving} onClick={handleDelete}>
              {getTranslation("EXCEPTION_REPORT_ACTION_DELETE") || "Delete"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
